import { Button, HStack, Spacer, VStack } from "@chakra-ui/react"
import { useScopedTranslation } from "@pathwright/ux/src/components/lng/useScopedTranslation"
import Link from "../../../../components/link/Link"
import { ActionType } from "../../../../lib/types"
import { useAuthContext } from "../../api/context"
import AuthRequestForm from "../../auth-request/AuthRequestForm"
import AuthFormIntentErrorAlert from "../../components/AuthFormIntentErrorAlert"
import AuthMotion from "../../components/AuthMotion"
import AuthOptionsDivider from "../../components/AuthOptionsDivider"
import AuthSpaceHeader from "../../components/AuthSpaceHeader"
import ExternalAuthMethods from "../../components/ExternalAuthMethods"
import { AuthIntentKeyWithError } from "../../utils/authIntent"
import { useAuthMethods } from "../../utils/authMethods"
import getAuthRoute from "../../utils/getAuthRoute"
import useColorModeBranding from "../../utils/useColorModeBranding"
import { SignInAuthRequestLoaderReturn } from "./api/loader"

const authIntentErrorKey: AuthIntentKeyWithError = "sign_in:without_password"

export type SignInAuthRequestViewProps = ActionType &
  SignInAuthRequestLoaderReturn

const SignInAuthRequestView = ({
  showPasswordOption,
  error,
  loading,
  onSubmit
}: SignInAuthRequestViewProps) => {
  const { t } = useScopedTranslation("auth.signin_request_view")
  const { allowSignup, userPropertyDefaults } = useAuthContext()
  const { externalAuthMethods } = useAuthMethods()
  const colorPalette = useColorModeBranding()

  return (
    <AuthMotion>
      <VStack gap={4} width="100%">
        <AuthSpaceHeader />
        <AuthFormIntentErrorAlert authIntentKey={authIntentErrorKey} />
        <AuthRequestForm
          userPropertyDefaults={userPropertyDefaults}
          error={error}
          loading={loading}
          onSubmit={onSubmit}
        />
        {(showPasswordOption ||
          !!externalAuthMethods.length ||
          allowSignup) && <AuthOptionsDivider />}
        {showPasswordOption && (
          <Button asChild variant="solid" colorPalette={colorPalette} w="full">
            <Link
              to={getAuthRoute("../password")}
              w="full"
              variant="plain"
              textDecor="none"
            >
              {t("sign_in_with_password")}
            </Link>
          </Button>
        )}
        {!!externalAuthMethods.length && (
          <ExternalAuthMethods authIntent={authIntentErrorKey} />
        )}
        {!!externalAuthMethods.length && allowSignup && <Spacer />}
        {allowSignup && (
          <HStack justifyContent="center">
            <Link
              colorPalette={colorPalette}
              textDecor="underline"
              whiteSpace="normal"
              display="inline"
              to={getAuthRoute("../../sign-up")}
            >
              {t("create_account")}
            </Link>
          </HStack>
        )}
      </VStack>
    </AuthMotion>
  )
}

export default SignInAuthRequestView
